import { useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { ComparisonRole } from "@/hooks/use-career-path";
import { type RoleLevelOption, useRoleLevelOptions } from "@/hooks/use-role-level-applicability";
import {
  type CapabilityExpectations,
  type LevelExpectations,
  useRoleLevelExpectations,
} from "@/hooks/use-role-level-expectations";
import { cn } from "@/lib/utils";

// Same affordance as the role detail page's level switcher (RoleLevelExpectations)
// — the levels a role uses are a short, role-scoped set, so they're buttons
// rather than a combobox, and a level the role doesn't use is absent entirely.
const LEVEL_BUTTON =
  "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1 text-xs font-medium transition-colors";
const LEVEL_ACTIVE = "border-primary bg-primary text-primary-foreground";
const LEVEL_INACTIVE = "border-input bg-card text-foreground hover:bg-muted/50";

// The two answer columns are the whole point of the table, and a horizontal
// row border alone lets a long left-hand answer read as though it continues
// into the right-hand one. A vertical rule down each keeps the split explicit
// and — with table-fixed's equal widths — visibly even.
const COL_SPLIT = "border-l";

// The level pickers must sit exactly over the columns they drive, so the picker
// strip and the table share one column geometry: a narrow leading column for
// the capability names, then the two answer columns. `1fr 2fr 2fr` on the grid
// and `w-1/5 w-2/5 w-2/5` on the table are the same 20/40/40 split, and both
// sit in an equally-wide bordered wrapper, so the rules line up down the page.
// Change one and you must change the other.
const GRID_COLS = "sm:grid-cols-[1fr_2fr_2fr]";
const TABLE_COL_LEAD = "w-1/5";
const TABLE_COL_ANSWER = "w-2/5";

/** One side of the comparison: a role, its applicable levels, and the pick. */
interface Side {
  role: ComparisonRole;
  /** Heading over the level buttons — the role name, or which side it is when both are one role. */
  heading: string;
  levels: RoleLevelOption[];
  activeId: string | null;
  onPick: (roleLevelId: string) => void;
  expectations: LevelExpectations | undefined;
}

/**
 * Rows are the union of both roles' capabilities. A capability the other role
 * doesn't require still gets a row — "not required over there" is exactly the
 * kind of gap someone reads this table to find.
 */
interface Row {
  key: string;
  name: string;
  kind: "skill" | "competency";
  from: CapabilityExpectations | undefined;
  to: CapabilityExpectations | undefined;
}

function buildRows(from: LevelExpectations | undefined, to: LevelExpectations | undefined): Row[] {
  const byKey = new Map<string, Row>();
  const add = (cap: CapabilityExpectations, side: "from" | "to") => {
    const key = `${cap.kind}-${cap.id}`;
    const existing = byKey.get(key);
    if (existing) {
      existing[side] = cap;
      return;
    }
    byKey.set(key, {
      key,
      name: cap.name,
      kind: cap.kind,
      from: side === "from" ? cap : undefined,
      to: side === "to" ? cap : undefined,
    });
  };
  for (const cap of from?.capabilities ?? []) add(cap, "from");
  for (const cap of to?.capabilities ?? []) add(cap, "to");

  // Skills before competencies (the grid's own order), alphabetical within.
  return [...byKey.values()].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "skill" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

/** Which side(s) require a capability — drives the row badge. */
function rowTone(row: Row): { label: string; className: string } | null {
  if (row.from && !row.to) {
    return {
      label: "Not required in target",
      className: "bg-warning-bg text-warning-foreground",
    };
  }
  if (!row.from && row.to) {
    return { label: "New", className: "bg-success-bg text-success-foreground" };
  }
  return null;
}

function LevelPicker({ side }: { side: Side }) {
  const active = side.levels.find((l) => l.id === side.activeId);
  return (
    // Its own card when stacked on a narrow screen; from `sm` up it becomes one
    // segment of the picker strip, so the wrapper owns the outer border and
    // background and this keeps only the rule that aligns with the table's
    // matching column divider.
    <div className="h-full space-y-1.5 rounded-lg border bg-muted/20 p-3 sm:rounded-none sm:border-0 sm:border-l sm:bg-transparent">
      <div className="text-sm font-medium">{side.heading}</div>
      <div className="flex flex-wrap gap-1.5">
        {side.levels.map((level) => {
          const isActive = level.id === side.activeId;
          return (
            <button
              key={level.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => side.onPick(level.id)}
              className={cn(LEVEL_BUTTON, isActive ? LEVEL_ACTIVE : LEVEL_INACTIVE)}
            >
              {level.name}
              {level.heldByViewer && (
                <span
                  className={cn(
                    "rounded-sm px-1 py-0.5 text-3xs font-semibold uppercase tracking-wide",
                    isActive ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground",
                  )}
                >
                  Your level
                </span>
              )}
            </button>
          );
        })}
      </div>
      {active?.definition && (
        <p className="text-xs leading-relaxed text-muted-foreground">{active.definition}</p>
      )}
    </div>
  );
}

/** A cell: the meets-expectations text, or why there isn't one. */
function ExpectationCell({
  cap,
  meetsOrdinal,
  roleName,
}: {
  cap: CapabilityExpectations | undefined;
  meetsOrdinal: number | null;
  /** This column's own role — the one that doesn't require the capability. */
  roleName: string;
}) {
  if (!cap) {
    return <span className="text-xs text-muted-foreground">Not required in {roleName}.</span>;
  }
  const text = meetsOrdinal === null ? undefined : cap.byOrdinal[meetsOrdinal];
  if (!text) return <span className="italic text-muted-foreground">Not written yet</span>;
  return <>{text}</>;
}

/**
 * Side-by-side "meets expectations" for every capability across the two roles
 * being compared, each at a chosen career level (EPIC-034 over WI-107's data).
 *
 * The bucket cards above answer *which* capabilities change; this answers what
 * the bar actually is on either side — the question someone eyeing a move
 * arrives with. Both reads are public (any signed-in user), so this needs no
 * gating of its own.
 *
 * Only the meets rung is shown. The other four rungs are performance-management
 * detail and would make this a ten-column table; the role detail page's grid is
 * where the full ladder lives.
 *
 * With `sameRole`, both sides are the one role and this IS the comparison — the
 * requirement buckets above are suppressed, the columns are labelled by level
 * rather than by role, and the target side defaults one rung up.
 */
export function ExpectationComparison({
  from,
  to,
  sameRole = false,
}: {
  from: ComparisonRole;
  to: ComparisonRole;
  /** Both sides are the one role — the levels are the whole comparison. */
  sameRole?: boolean;
}) {
  const fromLevels = useRoleLevelOptions(from.id);
  // Same role on both sides means one query, not two — asking twice for the
  // same id would be a wasted round trip (React Query would dedupe, but the
  // second hook still subscribes for nothing).
  const toLevels = useRoleLevelOptions(to.id, !sameRole);
  const [pickedFrom, setPickedFrom] = useState<string | null>(null);
  const [pickedTo, setPickedTo] = useState<string | null>(null);

  const fromApplied = (fromLevels.data ?? []).filter((l) => l.applies);
  const toApplied = sameRole ? fromApplied : (toLevels.data ?? []).filter((l) => l.applies);

  // Derived, not effect-seeded: a level disappearing from the role (or the
  // options arriving late) can't leave a dangling selection. The starting side
  // defaults to the level the viewer is actually at; the target side to its
  // lowest rung, which is where a move into it would land.
  const fromActive =
    pickedFrom !== null && fromApplied.some((l) => l.id === pickedFrom)
      ? pickedFrom
      : (fromApplied.find((l) => l.heldByViewer)?.id ?? fromApplied[0]?.id ?? null);
  // Within one role, defaulting the target to the lowest rung would put the
  // same level on both sides and show two identical columns. Step up one rung
  // from the starting level instead — the next move up is what's being asked.
  const sameRoleDefaultTo =
    toApplied[toApplied.findIndex((l) => l.id === fromActive) + 1]?.id ?? toApplied[0]?.id ?? null;
  const toActive =
    pickedTo !== null && toApplied.some((l) => l.id === pickedTo)
      ? pickedTo
      : sameRole
        ? sameRoleDefaultTo
        : (toApplied[0]?.id ?? null);

  const fromExpectations = useRoleLevelExpectations(from.id, fromActive);
  const toExpectations = useRoleLevelExpectations(to.id, toActive);

  const levelsLoading = fromLevels.isLoading || (!sameRole && toLevels.isLoading);
  if (levelsLoading) {
    return <Skeleton className="h-48 w-full rounded-lg" />;
  }

  /**
   * A FAILED read is not an empty ladder.
   *
   * Only loading was checked, so a dropped request rendered the emptiness
   * below as a statement of fact — "<role> has no career levels set" — on the
   * screen somebody uses to plan a move. Say what actually happened instead,
   * and offer the retry.
   */
  const levelsFailed = fromLevels.isError || (!sameRole && toLevels.isError);
  if (levelsFailed) {
    return (
      <ErrorState
        message="Career levels couldn't be loaded — this isn't a statement about the roles."
        onRetry={() => {
          fromLevels.refetch();
          if (!sameRole) toLevels.refetch();
        }}
      />
    );
  }

  // No ladder on either side means there's nothing level-specific to say. Name
  // the role that's missing one so it's actionable rather than a dead end.
  const missing = [
    fromApplied.length === 0 ? from.name : null,
    !sameRole && toApplied.length === 0 ? to.name : null,
  ].filter((n): n is string => n !== null);
  // One role with a single rung has a ladder, but not one you can compare
  // against itself — say so rather than rendering two identical columns.
  const singleRung = sameRole && fromApplied.length === 1;
  if (missing.length > 0 || singleRung) {
    return (
      <section className="rounded-lg border border-dashed bg-card px-4 py-6 text-center">
        <p className="text-sm font-medium">No career levels to compare</p>
        <p className="mx-auto mt-1 max-w-prose text-sm text-muted-foreground">
          {singleRung ? (
            `${from.name} only uses one career level, so there's nothing to line it up against — pick a different role on one side.`
          ) : (
            <>
              {sameRole
                ? `${from.name} has no career levels set, so there are no per-level expectations to line up.`
                : missing.length === 2
                  ? `Neither ${from.name} nor ${to.name} has career levels set, so there are no per-level expectations to line up.`
                  : `${missing[0]} has no career levels set, so there are no per-level expectations to line up against the other role.`}{" "}
              An admin sets a role's levels from the role page.
            </>
          )}
        </p>
      </section>
    );
  }

  const fromData = fromExpectations.data;
  const toData = toExpectations.data;
  // Both sides send the same constant; either will do, and null only while the
  // first response is still in flight.
  const meetsOrdinal = fromData?.meetsOrdinal ?? toData?.meetsOrdinal ?? null;
  const meetsName =
    fromData?.ratings.find((r) => r.ordinal === meetsOrdinal)?.name ??
    toData?.ratings.find((r) => r.ordinal === meetsOrdinal)?.name ??
    null;

  const sides: { from: Side; to: Side } = {
    from: {
      role: from,
      // Both sides carrying the same role name reads as a mistake — label them
      // by their part in the comparison instead.
      heading: sameRole ? "Starting level" : from.name,
      levels: fromApplied,
      activeId: fromActive,
      onPick: setPickedFrom,
      expectations: fromData,
    },
    to: {
      role: to,
      heading: sameRole ? "Target level" : to.name,
      levels: toApplied,
      activeId: toActive,
      onPick: setPickedTo,
      expectations: toData,
    },
  };

  const rows = buildRows(fromData, toData);
  const loading = fromExpectations.isLoading || toExpectations.isLoading;
  const failed = fromExpectations.isError || toExpectations.isError;

  return (
    <section className="space-y-4 rounded-lg border p-4">
      <div>
        <h3 className="text-sm font-semibold">
          {meetsName ? `What "${meetsName}" looks like` : "What meets expectations looks like"}
        </h3>
        <p className="text-sm text-muted-foreground">
          The bar for each capability at the level you pick on either side.{" "}
          {sameRole
            ? `Only the levels ${from.name} actually uses are offered.`
            : "Pick a level per role — only the levels each role actually uses are offered."}
        </p>
      </div>

      <div
        className={cn(
          "grid gap-3 sm:gap-0 sm:overflow-hidden sm:rounded-lg sm:border sm:bg-muted/20",
          GRID_COLS,
        )}
      >
        {/* Holds the strip's first column open so each picker lands over the
            answer column it drives, and labels the row while it's there. */}
        <div className="hidden px-3 py-3 text-sm font-medium text-muted-foreground sm:block">
          Career level
        </div>
        <LevelPicker side={sides.from} />
        <LevelPicker side={sides.to} />
      </div>

      {loading ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : failed ? (
        <ErrorState
          className="py-4"
          message="The expectations for these levels couldn't be loaded."
          onRetry={() => {
            fromExpectations.refetch();
            toExpectations.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
          {sameRole
            ? `${from.name} doesn't require any skills or competencies yet, so there are no expectations to compare.`
            : "Neither role requires any skills or competencies yet, so there are no expectations to compare."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          {/* Fixed layout so the two role columns stay equal width — the point
              is comparing them, and auto layout hands all the slack to whichever
              side happens to have longer text. */}
          <table className="w-full table-fixed text-sm">
            <caption className="sr-only">
              {meetsName ?? "Meets expectations"} for each capability required by {from.name} at{" "}
              {fromData?.roleLevelName} or {sameRole ? from.name : to.name} at{" "}
              {toData?.roleLevelName}.
            </caption>
            <thead>
              <tr className="border-b bg-muted/40">
                <th scope="col" className={cn("px-3 py-2 text-left font-medium", TABLE_COL_LEAD)}>
                  Capability
                </th>
                {/* Within one role the level IS the column's identity, so it
                    leads and the (identical) role name drops out. */}
                <th
                  scope="col"
                  className={cn("px-3 py-2 text-left font-medium", TABLE_COL_ANSWER, COL_SPLIT)}
                >
                  {sameRole ? (fromData?.roleLevelName ?? from.name) : from.name}
                  {!sameRole && fromData && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {fromData.roleLevelName}
                    </span>
                  )}
                </th>
                <th
                  scope="col"
                  className={cn("px-3 py-2 text-left font-medium", TABLE_COL_ANSWER, COL_SPLIT)}
                >
                  {sameRole ? (toData?.roleLevelName ?? to.name) : to.name}
                  {!sameRole && toData && (
                    <span className="block text-xs font-normal text-muted-foreground">
                      {toData.roleLevelName}
                    </span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const tone = rowTone(row);
                return (
                  <tr key={row.key} className="border-b align-top last:border-b-0">
                    <th scope="row" className="px-3 py-2.5 text-left font-medium">
                      {row.name}
                      <span className="block text-xs font-normal capitalize text-muted-foreground">
                        {row.kind}
                      </span>
                      {tone && (
                        <span
                          className={cn(
                            "mt-1 inline-block rounded-sm px-1.5 py-0.5 text-3xs font-semibold uppercase tracking-wide",
                            tone.className,
                          )}
                        >
                          {tone.label}
                        </span>
                      )}
                    </th>
                    <td className={cn("px-3 py-2.5 leading-relaxed", COL_SPLIT)}>
                      <ExpectationCell
                        cap={row.from}
                        meetsOrdinal={meetsOrdinal}
                        roleName={from.name}
                      />
                    </td>
                    <td className={cn("px-3 py-2.5 leading-relaxed", COL_SPLIT)}>
                      <ExpectationCell
                        cap={row.to}
                        meetsOrdinal={meetsOrdinal}
                        roleName={to.name}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
